import React, { useEffect, useState } from 'react';
import { Item, AccountDetail } from '../types';
import { getItems, getAccountDetails, syncItemsFromAccount, createItem, updateItem, deleteItem, updateItemMultiSpec, updateItemMultiQty } from '../services/api';
import { Box, RefreshCw, ShoppingBag, Edit, Trash2, Plus, Save, X } from 'lucide-react';

const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [editForm, setEditForm] = useState<Partial<Item>>({});
  const [addForm, setAddForm] = useState({
    item_id: '',
    item_title: '',
    item_price: '',
    item_image: '',
    is_multi_spec: false,
    is_multi_qty_ship: false
  });
  const [saving, setSaving] = useState(false);

  const loadData = () => {
    getItems().then(setItems);
    getAccountDetails().then(setAccounts);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSync = async () => {
      if (!selectedAccount) return alert('请先选择账号');
      setLoading(true);
      try {
        await syncItemsFromAccount(selectedAccount);
        getItems().then(setItems);
      } catch (error) {
        console.error('同步商品失败:', error);
        alert('同步失败，请重试');
      }
      setLoading(false);
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setEditForm({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    setSaving(true);
    try {
      await updateItem(selectedItem.cookie_id, selectedItem.item_id, editForm);
      setShowEditModal(false);
      loadData();
    } catch (error) {
      console.error('更新商品失败:', error);
      alert('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: Item) => {
    if (confirm(`确认删除商品"${item.item_title}"吗？`)) {
      try {
        await deleteItem(item.cookie_id, item.item_id);
        setItems(items.filter(i =>
          !(i.cookie_id === item.cookie_id && i.item_id === item.item_id)
        ));
      } catch (error) {
        console.error('删除商品失败:', error);
        alert('删除失败，请重试');
      }
    }
  };

  const handleAddItem = async () => {
    if (!addForm.item_id || !addForm.item_title) {
      alert('请填写商品ID和商品标题');
      return;
    }
    setSaving(true);
    try {
      await createItem(addForm.item_id, addForm);
      setShowAddModal(false);
      setAddForm({
        item_id: '',
        item_title: '',
        item_price: '',
        item_image: '',
        is_multi_spec: false,
        is_multi_qty_ship: false
      });
      loadData();
    } catch (error) {
      console.error('添加商品失败:', error);
      alert('添加失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const toggleMultiSpec = async (item: Item) => {
    try {
      const newVal = !item.is_multi_spec;
      await updateItemMultiSpec(item.cookie_id, item.item_id, newVal);
      setItems(items.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id
          ? { ...i, is_multi_spec: newVal }
          : i
      ));
    } catch (error) {
      console.error('切换多规格失败:', error);
    }
  };

  const toggleMultiQty = async (item: Item) => {
    try {
      const newVal = !item.is_multi_qty_ship;
      await updateItemMultiQty(item.cookie_id, item.item_id, newVal);
      setItems(items.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id
          ? { ...i, is_multi_qty_ship: newVal }
          : i
      ));
    } catch (error) {
      console.error('切换多数量发货失败:', error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">商品管理</h2>
          <p className="text-gray-500 mt-2 text-sm">监控并管理所有账号下的闲鱼商品。</p>
        </div>
        <div className="flex gap-3">
            <select
                className="ios-input px-4 py-3 rounded-xl text-sm"
                value={selectedAccount}
                onChange={e => setSelectedAccount(e.target.value)}
            >
                <option value="">选择账号以同步</option>
                {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.nickname}</option>
                ))}
            </select>
            <button
                onClick={handleSync}
                disabled={loading || !selectedAccount}
                className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 disabled:opacity-50"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                同步商品
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-5 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:bg-gray-800 transition-colors flex items-center gap-2 shadow-lg"
            >
              <Plus className="w-4 h-4" />
              添加商品
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map(item => (
              <div key={`${item.cookie_id}-${item.item_id}`} className="ios-card p-4 rounded-3xl hover:shadow-lg transition-all group relative">
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-[#FFE815] transition-colors"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-red-100 text-red-500 transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                  </div>
                  <div className="aspect-square bg-gray-100 rounded-2xl mb-4 overflow-hidden relative">
                      {item.item_image ? (
                          <img src={item.item_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <Box className="w-10 h-10" />
                          </div>
                      )}
                      <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded-lg">
                          ¥{item.item_price}
                      </div>
                  </div>
                  <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2 h-10">{item.item_title}</h3>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                      <span className="bg-gray-100 px-2 py-1 rounded-md truncate max-w-[100px]">ID: {item.item_id}</span>
                  </div>
                  <div className="flex gap-2">
                      <button
                        onClick={() => toggleMultiSpec(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_spec
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多规格
                      </button>
                      <button
                        onClick={() => toggleMultiQty(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_qty_ship
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多数量发货
                      </button>
                  </div>
              </div>
          ))}
          {items.length === 0 && (
             <div className="col-span-full py-20 text-center text-gray-400">
                 <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                 暂无商品数据，请选择账号进行同步
             </div>
          )}
      </div>

      {/* 添加商品弹窗 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">添加商品</h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 rounded-xl hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品ID *</label>
                <input
                  type="text"
                  value={addForm.item_id}
                  onChange={e => setAddForm({ ...addForm, item_id: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="闲鱼商品ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品标题 *</label>
                <input
                  type="text"
                  value={addForm.item_title}
                  onChange={e => setAddForm({ ...addForm, item_title: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="商品名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品价格</label>
                <input
                  type="text"
                  value={addForm.item_price}
                  onChange={e => setAddForm({ ...addForm, item_price: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="如: 19.90"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品图片URL</label>
                <input
                  type="text"
                  value={addForm.item_image}
                  onChange={e => setAddForm({ ...addForm, item_image: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addForm.is_multi_spec}
                    onChange={e => setAddForm({ ...addForm, is_multi_spec: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-700">多规格</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addForm.is_multi_qty_ship}
                    onChange={e => setAddForm({ ...addForm, is_multi_qty_ship: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-700">多数量发货</span>
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 rounded-xl font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleAddItem}
                disabled={saving}
                className="flex-1 ios-btn-primary px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? '添加中...' : <><Plus className="w-4 h-4" /> 添加</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑商品弹窗 */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">编辑商品</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 rounded-xl hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品ID</label>
                <input
                  type="text"
                  value={editForm.item_id || ''}
                  disabled
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm bg-gray-50 text-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品标题</label>
                <input
                  type="text"
                  value={editForm.item_title || ''}
                  onChange={e => setEditForm({ ...editForm, item_title: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="商品名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品价格</label>
                <input
                  type="text"
                  value={editForm.item_price || ''}
                  onChange={e => setEditForm({ ...editForm, item_price: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="如: 19.90"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">商品图片URL</label>
                <input
                  type="text"
                  value={editForm.item_image || ''}
                  onChange={e => setEditForm({ ...editForm, item_image: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-3 rounded-xl font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 ios-btn-primary px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? '保存中...' : <><Save className="w-4 h-4" /> 保存</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemList;
